import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Button, Dropdown, Input } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'
import { useT } from '@/shared/i18n'

// ---------------------------------------------------------------------------
// CommandPalette — surface-agnostic global command palette (Cmd+K).
//
// A dumb, presentational primitive: it owns open/close + keyboard + a11y, but
// NOT *what* it searches and NOT *where* navigation goes. The caller injects
// both — the `search` function (so the privileged /search URL never lives in
// this shared, public-bundle-reachable module) and the `navItems` list (the
// static page-navigation entries) plus an `onNavigate` callback. This keeps the
// import boundary clean: the component imports only shared/ + vendor and holds
// no surface knowledge.
//
// Behaviour (mirrors the prototype's .topbar__search pop):
//   - A trigger in the .topbar__search region shows a .kbd "⌘K" hint and, on
//     focus/click, opens the .search-pop overlay anchored under the input.
//   - A global metaKey/ctrlKey + 'k' listener focuses the trigger and opens the
//     pop — but never fires while focus is in another text field, so the
//     shortcut does not steal keystrokes from a form the user is filling in.
//   - Empty query → the (filtered) nav items under a "Go to" group. Non-empty
//     query → the four result groups (cases / people / companies / documents)
//     from `search`, debounced. No matches → an empty-result hint.
//   - Enter on the active row navigates (case → /cases/:slug, nav item → href);
//     ↑/↓ move the active row; Esc closes and restores focus to the trigger.
//
// Accessibility (APG combobox + listbox, native nonmodal manual popover):
//   - the input is role="combobox" with aria-expanded + aria-controls pointing
//     at the listbox and aria-activedescendant naming the active option
//   - the results container is role="listbox"; each row is role="option" with a
//     stable id and aria-selected on the active one
//   - focus is trapped within the input + pop; Escape closes and restores
//     trigger focus without opening again
//
// All user-facing strings come from i18n (chrome.topbar.search_*); the caller
// supplies already-translated nav-item labels.
// ---------------------------------------------------------------------------

// --- Result shapes supplied by the caller -----------------------------------

export interface CommandPaletteCase {
  uuid: string
  slug: string
  title: string
  case_type: string
  current_state: string
}

export interface CommandPalettePerson {
  uuid: string
  full_name: string
  email: string
}

export interface CommandPaletteCompany {
  uuid: string
  legal_name: string
  organization_number: string
}

export interface CommandPaletteDocument {
  uuid: string
  filename: string
  case_slug: string
  kind: string
}

/** Cross-entity search result, one array per entity group. */
export interface CommandPaletteGroups {
  cases: CommandPaletteCase[]
  people: CommandPalettePerson[]
  companies: CommandPaletteCompany[]
  documents: CommandPaletteDocument[]
}

/** A static page-navigation entry, shown when the query is empty (or matched). */
export interface CommandPaletteNavItem {
  /** Stable id (used for keying + the option's DOM id). */
  id: string
  /** Display label (already translated by the caller). */
  label: string
  /** Optional icon from the shared registry. */
  icon?: IconName
  /** Navigation target — followed on select. */
  href: string
  /** Optional extra match text (synonyms) so search can find the page. */
  keywords?: string
}

export interface CommandPaletteProps {
  /**
   * Resolves a query to result groups. Injected by the caller so the privileged
   * search URL stays out of this shared component. Called debounced; rejected
   * promises and races are handled internally.
   */
  search: (query: string) => Promise<CommandPaletteGroups>
  /** Static page-navigation entries (empty-query list + searchable). */
  navItems: CommandPaletteNavItem[]
  /** Called when a result/nav item is selected. The caller performs navigation. */
  onNavigate: (href: string) => void
  /** Optional placeholder override; defaults to the i18n search placeholder. */
  placeholder?: string
  /** Debounce window for the search call, ms. Defaults to 200. */
  debounceMs?: number
}

// A flat, navigable row — every visible option resolves to one of these so the
// keyboard model (a single active index + aria-activedescendant) stays simple.
type Row =
  | { kind: 'case'; key: string; href: string; data: CommandPaletteCase }
  | { kind: 'person'; key: string; href: string; data: CommandPalettePerson }
  | { kind: 'company'; key: string; href: string; data: CommandPaletteCompany }
  | { kind: 'document'; key: string; href: string; data: CommandPaletteDocument }
  | { kind: 'nav'; key: string; href: string; data: CommandPaletteNavItem }

const EMPTY_GROUPS: CommandPaletteGroups = {
  cases: [],
  people: [],
  companies: [],
  documents: [],
}

// Natively-focusable elements cycled through for the focus trap (mirrors Modal).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
}

// Is the user currently typing into a different text field? If so the global
// Cmd+K must not steal the keystroke (the prototype focuses unconditionally;
// we tighten that for forms).
function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return el.isContentEditable || !!el.closest('[contenteditable]:not([contenteditable="false"])')
}

export function CommandPalette({
  search,
  navItems,
  onNavigate,
  placeholder,
  debounceMs = 200,
}: CommandPaletteProps) {
  const t = useT()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [result, setResult] = useState<{ query: string; groups: CommandPaletteGroups } | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const openRef = useRef(false)
  const queryRef = useRef('')
  const restoringFocus = useRef(false)
  const ignoreDsClose = useRef(false)
  const searchVersion = useRef(0)
  const baseId = useId()
  const listboxId = `${baseId}-listbox`

  const trimmed = query.trim()
  const hasQuery = trimmed.length > 0
  const groups = result?.query === trimmed ? result.groups : EMPTY_GROUPS
  const pending = hasQuery && result?.query !== trimmed

  // ── Filtered nav items (empty query → all; query → label/keyword match) ────
  const filteredNav = useMemo(() => {
    if (!hasQuery) return navItems
    const needle = trimmed.toLowerCase()
    return navItems.filter((item) =>
      `${item.label} ${item.keywords ?? ''}`.toLowerCase().includes(needle),
    )
  }, [navItems, hasQuery, trimmed])

  // ── Flat row list, in render order, used for keyboard navigation ───────────
  const rows = useMemo<Row[]>(() => {
    if (!hasQuery) {
      return filteredNav.map((item) => ({
        kind: 'nav' as const,
        key: `nav-${item.id}`,
        href: item.href,
        data: item,
      }))
    }
    const result: Row[] = []
    for (const c of groups.cases) {
      result.push({ kind: 'case', key: `case-${c.uuid}`, href: `/cases/${c.slug}`, data: c })
    }
    for (const p of groups.people) {
      result.push({ kind: 'person', key: `person-${p.uuid}`, href: `/register/people/${p.uuid}`, data: p })
    }
    for (const co of groups.companies) {
      result.push({
        kind: 'company',
        key: `company-${co.uuid}`,
        href: `/register/companies/${co.uuid}`,
        data: co,
      })
    }
    for (const d of groups.documents) {
      result.push({
        kind: 'document',
        key: `document-${d.uuid}`,
        href: `/cases/${d.case_slug}`,
        data: d,
      })
    }
    for (const item of filteredNav) {
      result.push({ kind: 'nav', key: `nav-${item.id}`, href: item.href, data: item })
    }
    return result
  }, [hasQuery, filteredNav, groups])

  const highlighted = rows.findIndex((row) => row.key === activeKey)
  const activeIndex = Math.max(0, highlighted)
  const optionId = (index: number) => `${baseId}-opt-${rows[index]?.key}`

  // ── Debounced search ───────────────────────────────────────────────────────
  // A query change schedules a single `search` call after `debounceMs`. The
  // cleanup flag discards results from a stale (superseded) call so out-of-order
  // resolutions never clobber the latest query.
  const searchRef = useRef(search)
  useEffect(() => {
    searchRef.current = search
  }, [search])

  useEffect(() => {
    if (!open || !hasQuery) return
    let cancelled = false
    const version = searchVersion.current
    const handle = setTimeout(() => {
      if (cancelled || !openRef.current || version !== searchVersion.current) return
      void Promise.resolve()
        .then(() => {
          if (cancelled || !openRef.current || version !== searchVersion.current) return null
          return searchRef.current(trimmed)
        })
        .then((next) => {
          if (next && !cancelled && openRef.current && version === searchVersion.current) {
            setResult({ query: trimmed, groups: next })
          }
        })
        .catch(() => {
          if (!cancelled && openRef.current && version === searchVersion.current) {
            setResult({ query: trimmed, groups: EMPTY_GROUPS })
          }
        })
    }, debounceMs)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [open, hasQuery, trimmed, debounceMs])

  // ── Open / close + selection helpers ───────────────────────────────────────
  const closePop = useCallback(() => {
    if (!openRef.current) return
    openRef.current = false
    searchVersion.current++
    setOpen(false)
    setResult(null)
    const focused = document.activeElement
    if (wrapRef.current?.contains(focused) || popRef.current?.contains(focused)) {
      restoringFocus.current = true
      inputRef.current?.focus()
      restoringFocus.current = false
    }
  }, [])

  const openPop = useCallback(() => {
    if (restoringFocus.current) return
    openRef.current = true
    setOpen(true)
  }, [])

  useLayoutEffect(() => () => {
    openRef.current = false
    searchVersion.current++
  }, [])

  const select = useCallback(
    (row: Row | undefined) => {
      if (!openRef.current || !row || queryRef.current !== trimmed || !rows.includes(row)) return
      closePop()
      queryRef.current = ''
      setQuery('')
      setActiveKey(null)
      onNavigate(row.href)
    },
    [onNavigate, closePop, rows, trimmed],
  )

  // ── Global Cmd/Ctrl+K — focus the trigger + open, unless typing elsewhere ──
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.key.toLowerCase() !== 'k') return
      if (document.activeElement?.closest('dialog[open],[aria-modal="true"]')) return
      // Don't hijack the keystroke while the user types in another field.
      if (event.target !== inputRef.current && isEditableTarget(event.target)) return
      event.preventDefault()
      inputRef.current?.focus()
      openPop()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openPop])

  // ── Close on outside click ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return
      if (popRef.current?.contains(event.target as Node)) return
      closePop()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [open, closePop])

  // DS's capture click listener must not close the palette on its own input.
  useLayoutEffect(() => {
    const onClick = (event: MouseEvent) => {
      ignoreDsClose.current = !!wrapRef.current?.contains(event.target as Node)
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  const place = useCallback(() => {
    const pop = popRef.current
    const anchor = wrapRef.current
    if (!pop?.matches(':popover-open') || !anchor) return
    const rect = anchor.getBoundingClientRect()
    const below = Math.max(0, window.innerHeight - rect.bottom - 14)
    const above = Math.max(0, rect.top - 14)
    const onTop = below < Math.min(window.innerHeight * 0.6, pop.scrollHeight) && above > below
    const width = Math.min(rect.width, Math.max(0, window.innerWidth - 16))
    Object.assign(pop.style, {
      width: `${width}px`,
      maxHeight: `${Math.min(window.innerHeight * 0.6, onTop ? above : below)}px`,
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`,
    })
    const height = pop.getBoundingClientRect().height
    pop.style.top = `${Math.max(8, Math.min(onTop ? rect.top - height - 6 : rect.bottom + 6, window.innerHeight - height - 8))}px`
  }, [])

  useEffect(() => {
    if (!open) return
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, rows, place])

  useEffect(() => {
    if (open) popRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' })
  }, [open, activeIndex, rows])

  // ── Keyboard on the input (combobox model) ─────────────────────────────────
  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!open) openPop()
        setActiveKey(rows[Math.min(rows.length - 1, activeIndex + 1)]?.key ?? null)
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveKey(rows[Math.max(0, activeIndex - 1)]?.key ?? null)
        break
      case 'Home':
      case 'End':
        if (!open) break
        event.preventDefault()
        setActiveKey(rows[event.key === 'Home' ? 0 : rows.length - 1]?.key ?? null)
        break
      case 'Enter':
        event.preventDefault()
        select(rows[activeIndex])
        break
      case 'Escape':
        if (!open) break
        event.preventDefault()
        event.stopPropagation()
        closePop()
        break
      default:
        break
    }
  }

  // ── Focus trap within the input + pop (Tab / Shift+Tab cycle) ──────────────
  const onWrapKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      event.stopPropagation()
      closePop()
      return
    }
    if (event.key !== 'Tab' || !open) return
    const input = inputRef.current
    const pop = popRef.current
    if (!input) return
    const focusable = [input, ...(pop ? focusableWithin(pop) : [])]
    if (focusable.length < 2) return
    event.preventDefault()
    const index = focusable.indexOf(document.activeElement as HTMLElement)
    focusable[(index + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length]?.focus()
  }

  // ── Row renderer ───────────────────────────────────────────────────────────
  const renderRow = (row: Row, index: number, content: ReactNode) => {
    const isActive = index === activeIndex
    return (
      <Button
        variant="tertiary"
        key={row.key}
        id={optionId(index)}
        role="option"
        aria-selected={isActive}
        type="button"
        className={isActive ? 'search-pop__row is-active' : 'search-pop__row'}
        onMouseEnter={() => setActiveKey(row.key)}
        onFocus={() => setActiveKey(row.key)}
        onClick={(event) => { event.stopPropagation(); select(row) }}
      >
        {content}
      </Button>
    )
  }

  // Build the grouped, index-aligned content. We walk `rows` so the visual order
  // and the keyboard index always agree.
  const listContent: ReactNode[] = []

  const sectionRows = (
    kind: Row['kind'],
    label: string,
    render: (row: Row, index: number) => ReactNode,
  ) => {
    const slice = rows
      .map((row, index) => ({ row, index }))
      .filter((entry) => entry.row.kind === kind)
    if (slice.length === 0) return
    listContent.push(
      <div key={`sec-${kind}`} className="search-pop__sec" role="presentation">
        {label}
      </div>,
    )
    for (const { row, index } of slice) listContent.push(render(row, index))
  }

  if (hasQuery) {
    sectionRows('case', t('chrome.topbar.search_group_cases'), (row, index) => {
      const data = row.data as CommandPaletteCase
      return renderRow(row, index, (
        <>
          <span className="mono search-pop__id">{data.slug}</span>
          <span className="search-pop__t">{data.title}</span>
        </>
      ))
    })
    sectionRows('person', t('chrome.topbar.search_group_people'), (row, index) => {
      const data = row.data as CommandPalettePerson
      return renderRow(row, index, (
        <>
          <Icon name="user" size={14} aria-hidden="true" />
          <span className="search-pop__t">{data.full_name}</span>
          <span className="search-pop__meta">{data.email}</span>
        </>
      ))
    })
    sectionRows('company', t('chrome.topbar.search_group_companies'), (row, index) => {
      const data = row.data as CommandPaletteCompany
      return renderRow(row, index, (
        <>
          <Icon name="building" size={14} aria-hidden="true" />
          <span className="search-pop__t">{data.legal_name}</span>
          <span className="mono search-pop__meta">{data.organization_number}</span>
        </>
      ))
    })
    sectionRows('document', t('chrome.topbar.search_group_documents'), (row, index) => {
      const data = row.data as CommandPaletteDocument
      return renderRow(row, index, (
        <>
          <Icon name="doc" size={14} aria-hidden="true" />
          <span className="search-pop__t">{data.filename}</span>
        </>
      ))
    })
  }

  // Nav items render in both states (empty query → only group; with query → the
  // last group). Their indices already align because `rows` lists them last.
  sectionRows('nav', t('chrome.topbar.search_group_pages'), (row, index) => {
    const data = row.data as CommandPaletteNavItem
    return renderRow(row, index, (
      <>
        {data.icon != null && <Icon name={data.icon} size={14} aria-hidden="true" />}
        <span className="search-pop__t">{data.label}</span>
      </>
    ))
  })

  const showNoResults = hasQuery && !pending && rows.length === 0
  const showHint = !hasQuery && filteredNav.length === 0

  return (
    <div className="topbar__search" role="search" ref={wrapRef} onKeyDown={onWrapKeyDown}>
      <Icon name="search" size={14} className="topbar__search-icon" aria-hidden="true" />
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && rows[activeIndex] ? optionId(activeIndex) : undefined}
        aria-label={placeholder ?? t('chrome.topbar.search_placeholder')}
        placeholder={placeholder ?? t('chrome.topbar.search_placeholder')}
        value={query}
        onChange={(event) => {
          const next = event.target.value.trim()
          if (next !== queryRef.current) {
            searchVersion.current++
            setResult(null)
            setActiveKey(null)
          }
          queryRef.current = next
          setQuery(event.target.value)
          openPop()
        }}
        onFocus={openPop}
        onClick={openPop}
        onKeyDown={onInputKeyDown}
      />
      <span className="kbd" aria-hidden="true">
        {t('chrome.topbar.search_shortcut')}
      </span>

      {createPortal(
        <Dropdown
          asChild
          open={open}
          placement="none"
          onClose={() => { if (!ignoreDsClose.current) closePop() }}
          onToggle={place}
          id={listboxId}
          role="listbox"
          aria-busy={pending || undefined}
          aria-label={placeholder ?? t('chrome.topbar.search_placeholder')}
          className="search-pop"
          style={{ position: 'fixed', inset: 'auto', translate: 'none', margin: 0, minWidth: 0, overflowY: 'auto' }}
        >
          <div ref={popRef}>
            {showHint && <div className="search-pop__hint">{t('chrome.topbar.search_hint')}</div>}
            {showNoResults && (
              <div className="search-pop__hint">
                {t('chrome.topbar.search_no_results', { query: trimmed })}
              </div>
            )}
            {listContent}
          </div>
        </Dropdown>,
        wrapRef.current?.closest('[popover],dialog') ?? document.body,
      )}
    </div>
  )
}
